import React, { useEffect, useState, useCallback } from 'react';
import VisibilityEditor from '../components/VisibilityEditor';

const EMPTY_PADDLE = { name: '', description: '', imageUrl: '', purchaseUrl: '', sectionId: '', sortOrder: 0, price: '', custom: false, paddleShortName: '', shippingPrice: '', visibleTiers: [], visibleUserIds: [] };

function paddleVisibleUserIds(paddle) {
  if (Array.isArray(paddle.visibleUserIds)) return paddle.visibleUserIds;
  return paddle.visibleUserId ? [paddle.visibleUserId] : [];
}
const EMPTY_SECTION = { name: '' };

export default function AdminPaddlesPage() {
  const [paddles, setPaddles] = useState([]);
  const [sections, setSections] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_PADDLE);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [editingSection, setEditingSection] = useState(null);
  const [sectionForm, setSectionForm] = useState(EMPTY_SECTION);
  const [savingSection, setSavingSection] = useState(false);
  const [draggedSectionId, setDraggedSectionId] = useState(null);
  const [draggedPaddleId, setDraggedPaddleId] = useState(null);
  const [dragOverSectionId, setDragOverSectionId] = useState(null);
  const [dragOverPaddleId, setDragOverPaddleId] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const [paddlesRes, sectionsRes, usersRes] = await Promise.all([
        fetch('/api/manage-paddles', { credentials: 'include' }),
        fetch('/api/manage-sections?context=shop', { credentials: 'include' }),
        fetch('/api/manage-users', { credentials: 'include' }),
      ]);
      if (!paddlesRes.ok) throw new Error('Failed to load paddles');
      if (!sectionsRes.ok) throw new Error('Failed to load sections');
      setPaddles(await paddlesRes.json());
      setSections(await sectionsRes.json());
      if (usersRes.ok) setUsers(await usersRes.json());
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  // ─── Paddle CRUD ───
  const handleEdit = (paddle) => {
    setEditing(paddle);
    setForm({
      ...EMPTY_PADDLE,
      ...paddle,
      visibleTiers: paddle.visibleTiers || [],
      visibleUserIds: paddleVisibleUserIds(paddle),
    });
    setError('');
  };

  const handleNew = () => {
    setEditing('new');
    setForm(EMPTY_PADDLE);
    setError('');
  };

  const handleCancel = () => {
    setEditing(null);
    setForm(EMPTY_PADDLE);
    setError('');
  };

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');

    try {
      const isNew = editing === 'new';
      const url = isNew ? '/api/manage-paddles' : `/api/manage-paddles/${editing.id}`;
      const method = isNew ? 'POST' : 'PUT';

      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
        credentials: 'include',
      });

      if (!res.ok) {
        const text = await res.text();
        let msg = `Save failed (HTTP ${res.status})`;
        try { msg = JSON.parse(text).error || msg; } catch {}
        throw new Error(msg);
      }

      handleCancel();
      await fetchData();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (paddle) => {
    if (!window.confirm(`Delete "${paddle.name}"?`)) return;
    try {
      const res = await fetch(
        `/api/manage-paddles/${paddle.id}?tier=${encodeURIComponent(paddle.tier)}`,
        { method: 'DELETE', credentials: 'include' }
      );
      if (!res.ok) throw new Error('Delete failed');
      await fetchData();
    } catch (err) {
      setError(err.message);
    }
  };

  // Duplicates an item. The copy is created hidden (no section, no tiers, no
  // users) so it can be configured before anyone sees it.
  const handleDuplicate = async (paddle) => {
    setError('');
    try {
      const res = await fetch(`/api/manage-paddles/${paddle.id}/duplicate`, {
        method: 'POST',
        credentials: 'include',
      });
      if (!res.ok) {
        const text = await res.text();
        let msg = 'Duplicate failed';
        try { msg = JSON.parse(text).error || msg; } catch {}
        throw new Error(msg);
      }
      const copy = await res.json();
      await fetchData();
      handleEdit(copy);
    } catch (err) {
      setError(err.message);
    }
  };

  // ─── Section CRUD ───
  const handleNewSection = () => {
    setEditingSection('new');
    setSectionForm(EMPTY_SECTION);
    setError('');
  };

  const handleEditSection = (section) => {
    setEditingSection(section);
    setSectionForm({ name: section.name });
    setError('');
  };

  const handleCancelSection = () => {
    setEditingSection(null);
    setSectionForm(EMPTY_SECTION);
    setError('');
  };

  const handleSaveSection = async (e) => {
    e.preventDefault();
    setSavingSection(true);
    setError('');

    try {
      const isNew = editingSection === 'new';
      const url = isNew ? '/api/manage-sections' : `/api/manage-sections/${editingSection.id}`;
      const method = isNew ? 'POST' : 'PUT';

      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(sectionForm),
        credentials: 'include',
      });

      if (!res.ok) {
        const text = await res.text();
        let msg = 'Save failed';
        try { msg = JSON.parse(text).error || msg; } catch {}
        throw new Error(msg);
      }

      handleCancelSection();
      await fetchData();
    } catch (err) {
      setError(err.message);
    } finally {
      setSavingSection(false);
    }
  };

  const handleDeleteSection = async (section) => {
    if (!window.confirm(`Delete section "${section.name}"? Paddles in this section will become unassigned.`)) return;
    try {
      const res = await fetch(`/api/manage-sections/${section.id}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (!res.ok) throw new Error('Delete failed');
      await fetchData();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleSectionSortChange = async (section, field, direction) => {
    try {
      await fetch(`/api/manage-sections/${section.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sortField: field, sortDirection: direction }),
        credentials: 'include',
      });
      await fetchData();
    } catch (err) {
      setError(err.message);
    }
  };

  // ─── Drag-and-drop for sections ───
  const handleSectionDragStart = (e, sectionId) => {
    setDraggedSectionId(sectionId);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleSectionDragOver = (e, sectionId) => {
    e.preventDefault();
    if (draggedSectionId && sectionId !== draggedSectionId) {
      setDragOverSectionId(sectionId);
    }
  };

  const handleSectionDrop = async (e, targetSectionId) => {
    e.preventDefault();
    setDragOverSectionId(null);
    if (!draggedSectionId || draggedSectionId === targetSectionId) {
      setDraggedSectionId(null);
      return;
    }

    const newSections = [...sections];
    const dragIdx = newSections.findIndex(s => s.id === draggedSectionId);
    const dropIdx = newSections.findIndex(s => s.id === targetSectionId);
    const [moved] = newSections.splice(dragIdx, 1);
    newSections.splice(dropIdx, 0, moved);

    const reordered = newSections.map((s, i) => ({ ...s, order: i }));
    setSections(reordered);
    setDraggedSectionId(null);

    try {
      await fetch('/api/manage-sections-order', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sections: reordered.map(s => ({ id: s.id, order: s.order })) }),
        credentials: 'include',
      });
    } catch (err) {
      setError(err.message);
      await fetchData();
    }
  };

  const handleSectionDragEnd = () => {
    setDraggedSectionId(null);
    setDragOverSectionId(null);
  };

  // ─── Drag-and-drop for paddles within a section ───
  const handlePaddleDragStart = (e, paddleId) => {
    setDraggedPaddleId(paddleId);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handlePaddleDragOver = (e, paddleId) => {
    e.preventDefault();
    if (draggedPaddleId && paddleId !== draggedPaddleId) {
      setDragOverPaddleId(paddleId);
    }
  };

  const handlePaddleDrop = async (e, targetPaddleId, sectionId) => {
    e.preventDefault();
    setDragOverPaddleId(null);
    if (!draggedPaddleId || draggedPaddleId === targetPaddleId) {
      setDraggedPaddleId(null);
      return;
    }

    const sectionPaddles = paddles
      .filter(p => (p.sectionId || '') === sectionId)
      .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));

    const dragIdx = sectionPaddles.findIndex(p => p.id === draggedPaddleId);
    const dropIdx = sectionPaddles.findIndex(p => p.id === targetPaddleId);
    const [moved] = sectionPaddles.splice(dragIdx, 1);
    sectionPaddles.splice(dropIdx, 0, moved);

    const updates = sectionPaddles.map((p, i) => ({ ...p, sortOrder: i }));
    const newPaddles = paddles.map(p => {
      const updated = updates.find(u => u.id === p.id);
      return updated || p;
    });
    setPaddles(newPaddles);
    setDraggedPaddleId(null);

    try {
      for (const p of updates) {
        await fetch(`/api/manage-paddles/${p.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ sortOrder: p.sortOrder }),
          credentials: 'include',
        });
      }
    } catch (err) {
      setError(err.message);
      await fetchData();
    }
  };

  const handlePaddleDragEnd = () => {
    setDraggedPaddleId(null);
    setDragOverPaddleId(null);
  };

  if (loading) return <div className="page"><div className="loading">Loading...</div></div>;

  const visibilityLabel = (p) => {
    const userIds = paddleVisibleUserIds(p);
    const tiers = Array.isArray(p.visibleTiers) && p.visibleTiers.length
      ? p.visibleTiers
      : (p.tier && p.tier !== 'all' ? [p.tier] : []);

    if (tiers.length === 0 && userIds.length === 0) return 'Hidden';

    const parts = [];
    if (tiers.length > 0) parts.push(tiers.join(', '));
    if (userIds.length === 1) {
      const u = users.find((x) => x.id === userIds[0]);
      parts.push(u ? u.name : '1 user');
    } else if (userIds.length > 1) {
      parts.push(`${userIds.length} users`);
    }
    return parts.join(' + ');
  };

  const getPaddlesForSection = (sectionId) => {
    return paddles
      .filter(p => (p.sectionId || '') === sectionId)
      .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));
  };

  const unsectionedPaddles = paddles.filter(p => !p.sectionId || !sections.find(s => s.id === p.sectionId));

  return (
    <div className="page admin-page">
      <div className="admin-header">
        <h1>Manage Shop</h1>
        <div className="admin-header-actions">
          {!editing && <button className="btn btn-primary" onClick={handleNew}>+ Add Item</button>}
          {!editingSection && <button className="btn btn-secondary" onClick={handleNewSection}>+ Add Section</button>}
        </div>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      {editingSection && (
        <form className="admin-form" onSubmit={handleSaveSection}>
          <h2>{editingSection === 'new' ? 'New Section' : 'Edit Section'}</h2>
          <div className="form-group">
            <label htmlFor="sectionName">Section Name *</label>
            <input
              id="sectionName"
              value={sectionForm.name}
              onChange={(e) => setSectionForm({ ...sectionForm, name: e.target.value })}
              required
            />
          </div>
          <div className="form-actions">
            <button type="submit" className="btn btn-primary" disabled={savingSection}>
              {savingSection ? 'Saving...' : 'Save'}
            </button>
            <button type="button" className="btn btn-secondary" onClick={handleCancelSection}>Cancel</button>
          </div>
        </form>
      )}

      {editing && (
        <form className="admin-form" onSubmit={handleSave}>
          <h2>{editing === 'new' ? 'New Paddle' : 'Edit Paddle'}</h2>
          <div className="form-group">
            <label htmlFor="name">Name *</label>
            <input id="name" name="name" value={form.name} onChange={handleChange} required />
          </div>
          <div className="form-group">
            <label htmlFor="description">Description</label>
            <textarea id="description" name="description" value={form.description} onChange={handleChange} rows={3} />
          </div>
          <div className="form-group">
            <label htmlFor="imageUrl">Image URL</label>
            <input id="imageUrl" name="imageUrl" value={form.imageUrl} onChange={handleChange} />
          </div>
          <div className="form-group">
            <VisibilityEditor
              visibleTiers={form.visibleTiers || []}
              visibleUserIds={form.visibleUserIds || []}
              users={users}
              hiddenWhenEmpty
              onChange={({ visibleTiers, visibleUserIds }) => setForm({ ...form, visibleTiers, visibleUserIds })}
            />
          </div>
          <div className="form-group">
            <label htmlFor="sectionId">Section</label>
            <select id="sectionId" name="sectionId" value={form.sectionId} onChange={handleChange}>
              <option value="">No section</option>
              {sections.map(s => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label htmlFor="purchaseUrl">Purchase URL</label>
            <input id="purchaseUrl" name="purchaseUrl" value={form.purchaseUrl} onChange={handleChange} />
          </div>
          <div className="form-group">
            <label htmlFor="price">Price</label>
            <input id="price" name="price" type="number" step="0.01" min="0" value={form.price} onChange={handleChange} placeholder="e.g. 49.99" />
          </div>
          <div className="form-group form-group-checkbox">
            <label>
              <input
                type="checkbox"
                name="custom"
                checked={!!form.custom}
                onChange={(e) => setForm({ ...form, custom: e.target.checked })}
              />
              Custom Purchase Form
            </label>
          </div>
          {form.custom && (
            <>
              <div className="form-group">
                <label htmlFor="paddleShortName">Paddle Short Name</label>
                <input id="paddleShortName" name="paddleShortName" value={form.paddleShortName} onChange={handleChange} placeholder="e.g. ProV2" />
              </div>
              <div className="form-group">
                <label htmlFor="shippingPrice">Shipping Price</label>
                <input id="shippingPrice" name="shippingPrice" type="number" step="0.01" min="0" value={form.shippingPrice} onChange={handleChange} placeholder="e.g. 5.00" />
              </div>
            </>
          )}
          <div className="form-actions">
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Saving...' : 'Save'}
            </button>
            <button type="button" className="btn btn-secondary" onClick={handleCancel}>Cancel</button>
          </div>
        </form>
      )}

      <div className="sections-manager">
        <h2>Sections <span className="drag-hint">(drag to reorder)</span></h2>
        {sections.length === 0 ? (
          <p className="empty-state">No sections yet. Add a section to organize paddles.</p>
        ) : (
          <div className="sections-list">
            {sections.map((section) => (
              <div
                key={section.id}
                className={`section-item ${dragOverSectionId === section.id ? 'drag-over' : ''} ${draggedSectionId === section.id ? 'dragging' : ''}`}
                draggable
                onDragStart={(e) => handleSectionDragStart(e, section.id)}
                onDragOver={(e) => handleSectionDragOver(e, section.id)}
                onDrop={(e) => handleSectionDrop(e, section.id)}
                onDragEnd={handleSectionDragEnd}
              >
                <div className="section-header">
                  <span className="drag-handle">⠿</span>
                  <h3>{section.name}</h3>
                  <div className="section-controls">
                    <label className="sort-label">
                      Sort by:
                      <select
                        value={section.sortField || 'name'}
                        onChange={(e) => handleSectionSortChange(section, e.target.value, section.sortDirection || 'asc')}
                      >
                        <option value="name">Name</option>
                        <option value="tier">Tier</option>
                        <option value="sortOrder">Manual</option>
                      </select>
                    </label>
                    <label className="sort-label">
                      <select
                        value={section.sortDirection || 'asc'}
                        onChange={(e) => handleSectionSortChange(section, section.sortField || 'name', e.target.value)}
                      >
                        <option value="asc">Asc</option>
                        <option value="desc">Desc</option>
                      </select>
                    </label>
                    <button className="btn btn-sm" onClick={() => handleEditSection(section)}>Edit</button>
                    <button className="btn btn-sm btn-danger" onClick={() => handleDeleteSection(section)}>Delete</button>
                  </div>
                </div>

                <div className="section-paddles">
                  {getPaddlesForSection(section.id).length === 0 ? (
                    <p className="empty-state-sm">No paddles in this section</p>
                  ) : (
                    getPaddlesForSection(section.id).map((paddle) => (
                      <div
                        key={paddle.id}
                        className={`section-paddle-item ${dragOverPaddleId === paddle.id ? 'drag-over' : ''} ${draggedPaddleId === paddle.id ? 'dragging' : ''}`}
                        draggable
                        onDragStart={(e) => { e.stopPropagation(); handlePaddleDragStart(e, paddle.id); }}
                        onDragOver={(e) => { e.stopPropagation(); handlePaddleDragOver(e, paddle.id); }}
                        onDrop={(e) => { e.stopPropagation(); handlePaddleDrop(e, paddle.id, section.id); }}
                        onDragEnd={handlePaddleDragEnd}
                      >
                        <span className="drag-handle">⠿</span>
                        <span className="paddle-name">{paddle.name}</span>
                        <span className="visibility-tag">{visibilityLabel(paddle)}</span>
                        <div className="td-actions">
                          <button className="btn btn-sm" onClick={() => handleEdit(paddle)}>Edit</button>
                          <button className="btn btn-sm" onClick={() => handleDuplicate(paddle)}>Duplicate</button>
                          <button className="btn btn-sm btn-danger" onClick={() => handleDelete(paddle)}>Delete</button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {unsectionedPaddles.length > 0 && (
        <div className="sections-manager">
          <h2>Unsectioned Paddles</h2>
          <p className="admin-subtitle">
            Items without a section, tier, or user are hidden from the shop until you assign one.
          </p>
          <table className="admin-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Visibility</th>
                <th>Description</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {unsectionedPaddles.map((p) => (
                <tr key={p.id}>
                  <td>{p.name}</td>
                  <td>{visibilityLabel(p)}</td>
                  <td className="td-truncate">{p.description}</td>
                  <td className="td-actions">
                    <button className="btn btn-sm" onClick={() => handleEdit(p)}>Edit</button>
                    <button className="btn btn-sm" onClick={() => handleDuplicate(p)}>Duplicate</button>
                    <button className="btn btn-sm btn-danger" onClick={() => handleDelete(p)}>Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
